// ============================================================
// CommunitiesPage — liste des communautés de l'instance
// ============================================================

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import type { Community } from '@siverix/shared';
import { ErrorBox } from './OverviewPage';
import { Badge } from './UsersPage';
import { useSocket } from '../hooks/useSocket';

type Row = Community & { _count?: { members: number; channels: number } };

export function CommunitiesPage(): JSX.Element {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const { socket } = useSocket();

  async function fetchAll(): Promise<void> {
    try {
      setRows(await api.adminListCommunities());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void fetchAll();
  }, []);

  useEffect(() => {
    if (!socket) return;
    const refresh = (): void => void fetchAll();
    socket.on('community:created', refresh);
    socket.on('community:updated', refresh);
    socket.on('community:deleted', refresh);
    return () => {
      socket.off('community:created', refresh);
      socket.off('community:updated', refresh);
      socket.off('community:deleted', refresh);
    };
  }, [socket]);

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>Communautés</h1>
      <p style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
        Vue admin globale de l'ensemble des communautés hébergées.
      </p>

      {err && <ErrorBox message={err} />}
      {loading ? (
        <div>Chargement…</div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '14px',
          }}
        >
          {rows.map((c) => (
            <div
              key={c.id}
              style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: '10px',
                padding: '16px',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <strong style={{ fontSize: '15px' }}>{c.name}</strong>
                {c.isPublic ? (
                  <Badge label="PUBLIC" tone="success" />
                ) : (
                  <Badge label="PRIVÉE" tone="muted" />
                )}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: 4 }}>
                /{c.slug}
              </div>
              {c.description && <p style={{ fontSize: '13px', marginTop: 10 }}>{c.description}</p>}
              <div
                style={{
                  display: 'flex',
                  gap: '14px',
                  marginTop: 12,
                  fontSize: '12px',
                  color: 'var(--color-text-muted)',
                }}
              >
                <span>👤 {c._count?.members ?? c.memberCount ?? 0} membres</span>
                <span># {c._count?.channels ?? 0} salons</span>
              </div>
              <div
                style={{
                  marginTop: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  fontSize: '13px',
                }}
              >
                <Link to={`/communities/${c.id}/roles`} style={{ color: 'var(--color-accent)' }}>
                  ⚙️ Rôles & permissions →
                </Link>
                <Link to={`/communities/${c.id}/members`} style={{ color: 'var(--color-accent)' }}>
                  👥 Membres & rôles →
                </Link>
                <Link
                  to={`/communities/${c.id}/channels-acl`}
                  style={{ color: 'var(--color-accent)' }}
                >
                  🔒 ACL des salons →
                </Link>
              </div>
            </div>
          ))}
          {rows.length === 0 && (
            <div style={{ color: 'var(--color-text-muted)' }}>Aucune communauté.</div>
          )}
        </div>
      )}
    </div>
  );
}
